/*
  - Source: https://github.com/tharsis/evmosjs/blob/main/packages/proto/src/transaction/transaction.ts
  - Updated: support multiple messages
*/

import { Keccak } from "sha3";
import * as tx from "@tharsis/proto/dist/proto/cosmos/tx/v1beta1/tx";
import * as signing from "@tharsis/proto/dist/proto/cosmos/tx/signing/v1beta1/signing";
import * as coin from "@tharsis/proto/dist/proto/cosmos/base/v1beta1/coin";
import * as eth from "@tharsis/proto/dist/proto/ethermint/crypto/v1/ethsecp256k1/keys";
import * as secp from "@tharsis/proto/dist/proto/cosmos/crypto/secp256k1/keys";

import { createAnyMessage } from "@tharsis/proto/dist/messages/utils";

export const SIGN_DIRECT =
  signing.cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_DIRECT;
export const LEGACY_AMINO =
  signing.cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_LEGACY_AMINO_JSON;

export function createBody(messages, memo) {
  const msg = new tx.cosmos.tx.v1beta1.TxBody({
    messages: messages.map((item) => createAnyMessage(item)),
    memo,
  });
  return msg;
}

export function createFee(fee, denom, gasLimit) {
  return new tx.cosmos.tx.v1beta1.Fee({
    amount: [
      new coin.cosmos.base.v1beta1.Coin({
        denom,
        amount: fee,
      }),
    ],
    gas_limit: gasLimit,
  });
}

export function createSignerInfo(algo, publicKey, sequence, mode, path) {
  let pubkey;

  // NOTE: secp256k1 is going to be removed from evmos
  if (algo === "secp256k1") {
    pubkey = {
      message: new secp.cosmos.crypto.secp256k1.PubKey({
        key: publicKey,
      }),
      path: "cosmos.crypto.secp256k1.PubKey",
    };
  } else {
    // NOTE: assume ethsecp256k1 by default because after mainnet is the only one that is going to be supported
    pubkey = {
      message: new eth.ethermint.crypto.v1.ethsecp256k1.PubKey({
        key: publicKey,
      }),
      path: path || "ethermint.crypto.v1.ethsecp256k1.PubKey",
    };
  }

  const signerInfo = new tx.cosmos.tx.v1beta1.SignerInfo({
    public_key: createAnyMessage(pubkey),
    mode_info: new tx.cosmos.tx.v1beta1.ModeInfo({
      single: new tx.cosmos.tx.v1beta1.ModeInfo.Single({
        mode,
      }),
    }),
    sequence,
  });

  return signerInfo;
}

export function createAuthInfo(signerInfo, fee) {
  return new tx.cosmos.tx.v1beta1.AuthInfo({
    signer_infos: [signerInfo],
    fee,
  });
}

export function createSigDoc(bodyBytes, authInfoBytes, chainId, accountNumber) {
  return new tx.cosmos.tx.v1beta1.SignDoc({
    body_bytes: bodyBytes,
    auth_info_bytes: authInfoBytes,
    chain_id: chainId,
    account_number: accountNumber,
  });
}

export function createTransaction(
  messages,
  memo,
  fee,
  denom,
  gasLimit,
  algo,
  pubKey,
  path,
  sequence,
  accountNumber,
  chainId
) {
  const body = createBody(messages, memo);
  const feeMessage = createFee(fee, denom, gasLimit);
  const pubKeyDecoded = Buffer.from(pubKey, "base64");

  // AMINO
  const signInfoAmino = createSignerInfo(
    algo,
    new Uint8Array(pubKeyDecoded),
    sequence,
    LEGACY_AMINO,
    path
  );

  const authInfoAmino = createAuthInfo(signInfoAmino, feeMessage);

  const signDocAmino = createSigDoc(
    body.serializeBinary(),
    authInfoAmino.serializeBinary(),
    chainId,
    accountNumber
  );

  const hashAmino = new Keccak(256);
  hashAmino.update(Buffer.from(signDocAmino.serializeBinary()));
  const toSignAmino = hashAmino.digest("binary");

  // SignDirect
  const signInfoDirect = createSignerInfo(
    algo,
    new Uint8Array(pubKeyDecoded),
    sequence,
    SIGN_DIRECT,
    path
  );

  const authInfoDirect = createAuthInfo(signInfoDirect, feeMessage);

  const signDocDirect = createSigDoc(
    body.serializeBinary(),
    authInfoDirect.serializeBinary(),
    chainId,
    accountNumber
  );

  const hashDirect = new Keccak(256);
  hashDirect.update(Buffer.from(signDocDirect.serializeBinary()));
  const toSignDirect = hashDirect.digest("binary");

  return {
    legacyAmino: {
      body,
      authInfo: authInfoAmino,
      signBytes: toSignAmino.toString("base64"),
    },
    signDirect: {
      body,
      authInfo: authInfoDirect,
      signBytes: toSignDirect.toString("base64"),
    },
  };
}
